import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Car, Sparkles, Shield, HardHat, Trophy } from "lucide-react";
import { SafeHavenBannerCard } from "./SafeHavenCard";

const FeaturedEventsBanner = () => (
  <section className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 border-y-2 border-amber-500/60 py-5" data-testid="featured-events-banner">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="lg:col-span-2" data-testid="home-millsap-slot">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-sky-300 mb-3 flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" /> Leader Spotlight · UAW &amp; Detroit History
          </p>
          <Link
            to="/adrian-millsap"
            className="group flex items-stretch gap-0 rounded-xl overflow-hidden border-2 border-sky-400/50 hover:border-sky-300 bg-gradient-to-r from-[#0b1b33] via-slate-900 to-[#12325c] backdrop-blur transition-all hover:-translate-y-0.5 hover:shadow-xl"
            data-testid="home-millsap-card"
          >
            <div className="relative w-28 sm:w-36 flex-shrink-0 overflow-hidden">
              <img
                src="/adrian-millsap.jpg"
                alt="Adrian Millsap, UAW International Representative"
                className="absolute inset-0 w-full h-full object-cover object-top"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0b1b33]/80" />
              <img
                src="/uaw-logo.png"
                alt="UAW"
                className="absolute bottom-1.5 left-1.5 w-9 h-9 rounded-full border border-white/30 object-contain"
                loading="lazy"
              />
            </div>
            <div className="p-4 flex flex-col justify-center min-w-0">
              <span className="text-[10px] font-black tracking-widest uppercase text-sky-300">
                Adrian Millsap · UAW International Rep · Organizing
              </span>
              <h3 className="text-white font-black text-base sm:text-lg leading-tight mt-1">
                He Gave The Dream In Detroit First
              </h3>
              <p className="text-white/80 text-xs sm:text-sm mt-1">
                June 23, 1963: 200,000 people down Woodward Avenue, and Dr. King preaching
                “I Have a Dream” inside Cobo Hall two months before Washington — recorded by Motown,
                organized alongside the UAW. Flint, the Rouge, $160,000 of Birmingham bail, and
                Reuther's $50,000 check to the Memphis sanitation strikers.
              </p>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold mt-2 text-sky-300 group-hover:underline underline-offset-4">
                Read the history behind the card <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </Link>
        </div>
        <SafeHavenBannerCard />
        <div data-testid="home-stand-strong-slot">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-teal-300 mb-3 flex items-center gap-2">
            <Shield className="w-3.5 h-3.5" /> Stand Strong · Bullying Help
          </p>
          <Link
            to="/stand-strong"
            className="group flex items-center gap-4 rounded-xl border-2 border-teal-400/50 hover:border-teal-300 bg-gradient-to-r from-teal-900/50 to-indigo-900/50 backdrop-blur transition-all hover:-translate-y-0.5 hover:shadow-xl p-4 h-[calc(100%-2rem)]"
            data-testid="home-stand-strong-card"
          >
            <div className="w-12 h-12 rounded-full bg-teal-400/20 flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-teal-300" />
            </div>
            <div className="min-w-0">
              <span className="text-[10px] font-black tracking-widest uppercase text-teal-300">Kids · Teens · Adults</span>
              <h3 className="text-white font-black text-base sm:text-lg leading-tight mt-1">Being bullied at school, online, or at work?</h3>
              <p className="text-white/70 text-xs sm:text-sm mt-0.5">
                Free 24/7 help lines, a game plan that works, and real support for hard thoughts. You're not alone.
              </p>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold mt-2 text-teal-300 group-hover:underline underline-offset-4">
                Stand strong with us <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </Link>
        </div>
        <div data-testid="home-jerry-reese-slot">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-400 mb-3 flex items-center gap-2">
            <Trophy className="w-3.5 h-3.5" /> Lake County to the Super Bowl
          </p>
          <Link
            to="/jerry-reese"
            className="group flex items-stretch gap-0 rounded-xl overflow-hidden border-2 border-[#a71930]/70 hover:border-[#a71930] bg-[#0b2265]/40 backdrop-blur transition-all hover:-translate-y-0.5 hover:shadow-xl h-[calc(100%-2rem)]"
            data-testid="home-jerry-reese-card"
          >
            <img
              src="/jerry-dallas-bright.jpg"
              alt="Jerry Reese with Kevin Bradshaw on the field in Dallas"
              className="w-28 sm:w-32 object-cover object-top flex-shrink-0"
              loading="lazy"
            />
            <div className="p-4 flex flex-col justify-center min-w-0">
              <span className="text-[10px] font-black tracking-widest uppercase text-amber-300">
                Tiptonville, TN · Two Super Bowl Rings
              </span>
              <h3 className="text-white font-black text-base sm:text-lg leading-tight mt-1">
                Jerry Reese — A League of His Own
              </h3>
              <p className="text-white/70 text-xs sm:text-sm mt-0.5">
                From a 1-A state championship in Lake County to the first Black GM in NFL history to win a Super Bowl
                — then he came home and co-founded Team 4 Success. Photos, video and the bio you have not read online.
              </p>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold mt-2 text-amber-300 group-hover:underline underline-offset-4">
                Read his story <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </Link>
        </div>
        <div data-testid="home-d10-enroll-slot">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-300 mb-3 flex items-center gap-2">
            <HardHat className="w-3.5 h-3.5" /> District 10 Trades Pathways · Now Enrolling
          </p>
          <Link
            to="/register"
            className="group flex items-stretch gap-0 rounded-xl overflow-hidden border-2 border-amber-400/60 hover:border-amber-300 bg-gradient-to-r from-amber-950/60 to-slate-900 backdrop-blur transition-all hover:-translate-y-0.5 hover:shadow-xl h-[calc(100%-2rem)]"
            data-testid="home-d10-enroll-card"
          >
            <div className="relative w-28 sm:w-32 flex-shrink-0 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1621905252507-b35492cc74b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"
                alt="Apprentice in a hard hat on a job site"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-slate-900/70" />
              <span className="absolute bottom-0 left-0 right-0 bg-amber-400 text-slate-900 text-[9px] font-black uppercase tracking-widest text-center py-1">
                100% Free
              </span>
            </div>
            <div className="p-4 flex flex-col justify-center min-w-0">
              <span className="text-[10px] font-black tracking-widest uppercase text-amber-300">
                4 Weeks · MC3-Aligned · Ages 18+
              </span>
              <h3 className="text-white font-black text-base sm:text-lg leading-tight mt-1" data-testid="home-d10-enroll-headline">
                Learn a Trade. Build Your Future.
              </h3>
              <p className="text-amber-100 text-xs sm:text-sm font-semibold mt-1">
                Your pathway starts here — enroll in District 10 Trades Pathways today.
              </p>
              <p className="text-white/70 text-xs mt-1">
                Free pre-apprenticeship: OSHA-10, tools and math, trade interviews. No experience needed, PPE provided.
              </p>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold mt-2 text-amber-300 group-hover:underline underline-offset-4">
                Enroll free in 2 minutes <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </Link>
        </div>
        <div className="lg:col-span-2" data-testid="home-uan-slot">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-amber-300 mb-3 flex items-center gap-2">
            <Car className="w-3.5 h-3.5" /> Union Member Benefit · Union Auto Network
          </p>
          <Link
            to="/union-auto-network"
            className="group flex items-center gap-4 rounded-xl border-2 border-amber-400/50 hover:border-amber-300 bg-gradient-to-r from-slate-900 via-slate-900 to-amber-950/60 backdrop-blur transition-all hover:-translate-y-0.5 hover:shadow-xl p-4"
            data-testid="home-uan-card"
          >
            <img
              src="/share/union-auto-network-logo.png"
              alt="Union Auto Network"
              className="w-14 h-14 rounded-full bg-white object-contain p-1 flex-shrink-0"
            />
            <div className="min-w-0">
              <span className="text-[10px] font-black tracking-widest uppercase text-amber-300">
                Members &amp; Immediate Family
              </span>
              <h3 className="text-white font-black text-base sm:text-lg leading-tight mt-1">A Better Way To Buy</h3>
              <p className="text-white/70 text-xs sm:text-sm mt-0.5">
                Negotiated member pricing on a new or used vehicle through a network of authorized dealers, with a
                support team that walks you through the whole purchase.
              </p>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold mt-2 text-amber-300 group-hover:underline underline-offset-4">
                See the member benefit <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export { FeaturedEventsBanner };
